import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { PoButtonModule } from '@po-ui/ng-components';
import { assert, AtsUiLibModule, LoaderService } from 'ats-ui-lib';
import { JobApplicationService } from '../../root';

interface RouteParams {
  jobApplicationRecommendationConfirmationId: string;
  shouldAccept: boolean;
  secret: string;
  valid: true;
}

@Component({
  selector: 'ats-job-application-recommendation-confirmation',
  imports: [PoButtonModule, RouterModule, TranslateModule, AtsUiLibModule],
  templateUrl: './job-application-recommendation-confirmation.component.html',
  styleUrl: './job-application-recommendation-confirmation.component.css',
})
export class JobApplicationRecommendationConfirmationComponent implements OnInit {
  status = signal<'accepted' | 'rejected' | 'already-decided' | null>(null);
  confirmationResult = computed<{
    mainImage: string;
    title: string;
    subtitle: string;
  } | null>(() => {
    const basePath = 'assets/images';
    switch (this.status()) {
      case 'accepted':
        return {
          mainImage: `${basePath}/banner-professional-purpose.svg`,
          title: 'l-recommendation-confirmation-accept-title',
          subtitle: 'l-recommendation-confirmation-accept-subtitle',
        };
      case 'rejected':
        return {
          mainImage: `${basePath}/imagem_pessoa_sentada.svg`,
          title: 'l-recommendation-confirmation-reject-title',
          subtitle: 'l-recommendation-confirmation-reject-subtitle',
        };
      case 'already-decided':
        return {
          mainImage: `${basePath}/sitting-6.svg`,
          title: 'l-recommendation-confirmation-already-decided-title',
          subtitle: 'l-recommendation-confirmation-already-decided-subtitle',
        };
      default:
        return null;
    }
  });

  constructor(
    private jobApplicationService: JobApplicationService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private loaderService: LoaderService,
  ) {}

  async ngOnInit(): Promise<void> {
    const params = this.getParamsFromRoute();
    if (!params.valid) {
      await this.goToHome();
      return;
    }
    await this.decide(params);
  }

  private async decide(params: RouteParams): Promise<void> {
    try {
      this.loaderService.show();
      await this.jobApplicationService.decideJobApplicationRecommendationConfirmation(
        params.jobApplicationRecommendationConfirmationId,
        params.secret,
        params.shouldAccept,
      );
      this.status.set(params.shouldAccept ? 'accepted' : 'rejected');
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 404) {
        this.status.set('already-decided');
      } else {
        await this.goToHome();
        throw error;
      }
    } finally {
      this.loaderService.hide();
    }
  }

  private getParamsFromRoute(): RouteParams | { valid: false } {
    const activatedRouteSnapshot = this.activatedRoute.snapshot;
    const jobApplicationRecommendationConfirmationId = activatedRouteSnapshot.paramMap
      .get('id')
      ?.toString();
    const shouldAccept: boolean = activatedRouteSnapshot.data['shouldAccept'];
    assert(typeof shouldAccept === 'boolean', 'shouldAccept route data must be a boolean');
    const secret = activatedRouteSnapshot.queryParamMap.get('secret');
    const isInvalidRoute =
      jobApplicationRecommendationConfirmationId == null || shouldAccept == null || secret == null;
    if (isInvalidRoute) {
      return { valid: false };
    }
    return { jobApplicationRecommendationConfirmationId, shouldAccept, secret, valid: true };
  }

  async goToHome(): Promise<void> {
    await this.router.navigate(['register-user']);
  }
}
